import React, { useState, useEffect } from 'react';
import {
    Table, Button, Space, message, Spin,
    Tooltip, Popconfirm, Rate, Modal
} from 'antd';
import {
    DeleteOutlined, StarFilled, ExpandOutlined, MessageOutlined
} from '@ant-design/icons';
import { motion } from 'framer-motion';
import adminService from '../../services/adminService';

const FeedbackPage = () => {
    const [feedbacks, setFeedbacks] = useState([]);
    const [stats, setStats] = useState({ averageRating: 0, totalCount: 0, distribution: {} });
    const [loading, setLoading] = useState(false);
    const [ratingFilter, setRatingFilter] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);

    // Preview modal
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewContent, setPreviewContent] = useState('');

    const fetchData = async () => {
        setLoading(true);
        try {
            const [feedbackData, statsData] = await Promise.all([
                adminService.getFeedback(ratingFilter, currentPage, pageSize),
                adminService.getFeedbackStats(),
            ]);
            setFeedbacks(feedbackData.content);
            setTotalElements(feedbackData.totalElements);
            setStats(statsData);
        } catch (err) {
            message.error('Veriler yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [ratingFilter, currentPage, pageSize]);

    const handleDelete = async (id) => {
        try {
            await adminService.deleteFeedback(id);
            message.success('Geri bildirim silindi');
            fetchData();
        } catch (err) {
            message.error('Silme hatası');
        }
    };

    const handlePreview = (content) => {
        setPreviewContent(content);
        setPreviewVisible(true);
    };

    const dist = stats.distribution || {};

    const columns = [
        {
            title: 'Puan',
            dataIndex: 'rating',
            key: 'rating',
            width: 140,
            render: (rating) => (
                <Rate disabled defaultValue={rating} style={{ fontSize: 14 }} />
            ),
        },
        {
            title: 'Yorum',
            dataIndex: 'comment',
            key: 'comment',
            width: 280,
            render: (text) => (
                text ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                            color: 'var(--admin-text-secondary)',
                            maxWidth: 240,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'inline-block',
                            fontSize: 13,
                        }}>
                            {text.substring(0, 80)}{text.length > 80 ? '...' : ''}
                        </span>
                        {text.length > 80 && (
                            <Tooltip title="Tam yorumu gör">
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<ExpandOutlined />}
                                    onClick={() => handlePreview(text)}
                                    style={{ color: 'var(--admin-accent-primary)' }}
                                />
                            </Tooltip>
                        )}
                    </div>
                ) : (
                    <span style={{ color: 'var(--admin-text-tertiary)', fontStyle: 'italic', fontSize: 13 }}>
                        Yorum yok
                    </span>
                )
            ),
        },
        {
            title: 'Müşteri',
            dataIndex: 'customerName',
            key: 'customerName',
            width: 150,
            render: (name) => (
                <span style={{ color: 'var(--admin-text-primary)', fontWeight: 500, fontSize: 13 }}>
                    {name || '-'}
                </span>
            ),
        },
        {
            title: 'Tarih',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 150,
            render: (date) => (
                date ? (
                    <span style={{ color: 'var(--admin-text-tertiary)', fontSize: 12 }}>
                        {new Date(date).toLocaleString('tr-TR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                        })}
                    </span>
                ) : '-'
            ),
        },
        {
            title: 'İşlemler',
            key: 'actions',
            fixed: 'right',
            width: 80,
            render: (_, record) => (
                <Space size={4}>
                    <Tooltip title="Sil">
                        <Popconfirm
                            title="Bu geri bildirimi silmek istediğinize emin misiniz?"
                            onConfirm={() => handleDelete(record.id)}
                            okText="Sil"
                            cancelText="İptal"
                        >
                            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                                <Button
                                    className="admin-btn-icon"
                                    icon={<DeleteOutlined />}
                                    danger
                                />
                            </motion.div>
                        </Popconfirm>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div>
            {/* Page Header */}
            <div className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">Geri Bildirimler</h1>
                    <p className="admin-page-subtitle">
                        Müşterilerin sohbet sonrası verdiği puanları ve yorumları yönetin.
                    </p>
                </div>
            </div>

            {/* Stats Cards */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
                <motion.div
                    whileHover={{ scale: 1.02 }}
                    style={{
                        flex: 1,
                        background: 'rgba(245, 158, 11, 0.1)',
                        border: '1px solid rgba(245, 158, 11, 0.2)',
                        borderRadius: 8,
                        padding: '16px 20px',
                    }}
                >
                    <div style={{ color: 'var(--admin-text-tertiary)', fontSize: 13 }}>Ortalama Puan</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                        <span style={{ color: '#f59e0b', fontSize: 28, fontWeight: 700 }}>
                            {stats.averageRating || 0}
                        </span>
                        <StarFilled style={{ color: '#f59e0b', fontSize: 18 }} />
                    </div>
                </motion.div>
                <motion.div
                    whileHover={{ scale: 1.02 }}
                    style={{
                        flex: 1,
                        background: 'rgba(99, 102, 241, 0.1)',
                        border: '1px solid rgba(99, 102, 241, 0.2)',
                        borderRadius: 8,
                        padding: '16px 20px',
                    }}
                >
                    <div style={{ color: 'var(--admin-text-tertiary)', fontSize: 13 }}>Toplam</div>
                    <div style={{ color: '#6366f1', fontSize: 28, fontWeight: 700, marginTop: 4 }}>
                        {stats.totalCount || 0}
                    </div>
                </motion.div>
                <motion.div
                    whileHover={{ scale: 1.02 }}
                    style={{
                        flex: 1,
                        background: 'rgba(34, 197, 94, 0.1)',
                        border: '1px solid rgba(34, 197, 94, 0.2)',
                        borderRadius: 8,
                        padding: '16px 20px',
                    }}
                >
                    <div style={{ color: 'var(--admin-text-tertiary)', fontSize: 13, marginBottom: 6 }}>Dağılım</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {[5, 4, 3, 2, 1].map((r) => (
                            <span key={r} style={{ fontSize: 12, color: 'var(--admin-text-secondary)' }}>
                                {r}<StarFilled style={{ fontSize: 10, color: '#f59e0b', margin: '0 1px 0 1px' }} />
                                <strong>{dist[r] || 0}</strong>
                                {r > 1 && <span style={{ margin: '0 2px', color: 'var(--admin-text-tertiary)' }}>·</span>}
                            </span>
                        ))}
                    </div>
                </motion.div>
            </div>

            {/* Rating Filter */}
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--admin-text-tertiary)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginRight: 4 }}>Filtre</span>
                <button
                    onClick={() => { setRatingFilter(null); setCurrentPage(0); }}
                    style={{
                        padding: '6px 14px',
                        borderRadius: 8,
                        border: `1px solid ${!ratingFilter ? 'rgba(99, 102, 241, 0.5)' : 'var(--admin-border-color)'}`,
                        background: !ratingFilter ? 'rgba(99, 102, 241, 0.15)' : 'var(--admin-glass-bg)',
                        color: !ratingFilter ? '#818cf8' : 'var(--admin-text-secondary)',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontFamily: 'inherit',
                    }}
                >
                    Tümü <span style={{ opacity: 0.7, marginLeft: 2 }}>{stats.totalCount || 0}</span>
                </button>
                {[5, 4, 3, 2, 1].map((r) => {
                    const isActive = ratingFilter === r;
                    return (
                        <button
                            key={r}
                            onClick={() => { setRatingFilter(isActive ? null : r); setCurrentPage(0); }}
                            style={{
                                padding: '6px 12px',
                                borderRadius: 8,
                                border: `1px solid ${isActive ? 'rgba(245, 158, 11, 0.5)' : 'var(--admin-border-color)'}`,
                                background: isActive ? 'rgba(245, 158, 11, 0.15)' : 'var(--admin-glass-bg)',
                                color: isActive ? '#fbbf24' : 'var(--admin-text-secondary)',
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                fontFamily: 'inherit',
                            }}
                        >
                            {r} <StarFilled style={{ fontSize: 10, color: isActive ? '#fbbf24' : 'var(--admin-text-tertiary)' }} />
                            <span style={{ opacity: 0.7 }}>{dist[r] || 0}</span>
                        </button>
                    );
                })}
            </div>

            {/* Table */}
            <motion.div
                className="admin-table-container"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
            >
                {loading ? (
                    <div className="admin-loading">
                        <Spin size="large" />
                    </div>
                ) : (
                    <Table
                        scroll={{ x: 'max-content' }}
                        columns={columns}
                        dataSource={feedbacks}
                        rowKey="id"
                        pagination={{
                            current: currentPage + 1,
                            pageSize: pageSize,
                            total: totalElements,
                            onChange: (page, size) => {
                                setCurrentPage(page - 1);
                                setPageSize(size);
                            },
                            showSizeChanger: true,
                            showTotal: (total) => `Toplam ${total} kayıt`,
                        }}
                    />
                )}
            </motion.div>

            {/* Preview Modal */}
            <Modal
                title={
                    <div className="admin-modal-heading">
                        <div className="admin-modal-heading-icon">
                            <MessageOutlined />
                        </div>
                        <div>
                            <div className="admin-modal-heading-title">Yorum</div>
                            <div className="admin-modal-heading-subtitle">
                                Müşterinin bıraktığı yorumun tamamı
                            </div>
                        </div>
                    </div>
                }
                open={previewVisible}
                onCancel={() => setPreviewVisible(false)}
                footer={null}
                width="92vw"
                style={{ maxWidth: 500 }}
                className="admin-modal"
                rootClassName="admin-modal"
            >
                <div style={{
                    background: 'var(--admin-bg-tertiary)',
                    borderRadius: 8,
                    padding: 20,
                    maxHeight: 400,
                    overflowY: 'auto',
                    whiteSpace: 'pre-wrap',
                    fontSize: 14,
                    lineHeight: 1.7,
                    color: 'var(--admin-text-secondary)',
                }}>
                    {previewContent}
                </div>
            </Modal>
        </div>
    );
};

export default FeedbackPage;
